// Where an authenticated user belongs when they click "home" (the navbar brand)
// or land on "/" with a live session.
//
// Students must never be sent to the parent dashboard: it reports on whichever
// child the *parent* picked (localStorage `selectedStudentID`), a key the student
// login flow never sets. A student who lands there sees an empty dashboard, or on
// a shared family device a sibling's id left over from the parent's session.
// (The backend refuses cross-student reads, so that is a broken view rather than
// a data leak — but it is still not a screen a student should ever see.)

export const STUDENT_HOME_PATH = '/TakeTests';
export const PARENT_HOME_PATH = '/parent-dashboard';

const ROLE_STORAGE_KEY = 'studyapp_role';

const normaliseRole = (role) => String(role || '').trim().toLowerCase();

/** True only for a confirmed student role. Unknown/missing roles are not students. */
export function isStudentRole(role) {
  return normaliseRole(role) === 'student';
}

/**
 * Home path for a given role. Deliberately a deny-list rather than an allow-list:
 * only a confirmed student is redirected away from the dashboard, so a parent or
 * teacher whose role string is missing or unexpected keeps the behaviour they
 * have today instead of being bounced out of their own dashboard.
 */
export function getRoleHomePath(role) {
  return isStudentRole(role) ? STUDENT_HOME_PATH : PARENT_HOME_PATH;
}

/** Read the stored role directly — for callers that don't already hold user state. */
export function getStoredRole() {
  if (typeof window === 'undefined') {
    return null;
  }
  try {
    return window.localStorage.getItem(ROLE_STORAGE_KEY);
  } catch (error) {
    // Storage can throw in private-mode/blocked-cookie browsers; fall back to the
    // non-student default rather than breaking navigation.
    return null;
  }
}

export function getStoredRoleHomePath() {
  return getRoleHomePath(getStoredRole());
}

/** The parent dashboard is parent/teacher/admin territory; students are redirected. */
export function canViewParentDashboard(role) {
  return !isStudentRole(role);
}
